import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';
import { Modal, Spinner } from '../ui.js';

export function CopyRow({ label, hint, link }: { label: string; hint: string; link: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be denied; the input is selectable as a fallback.
      toast.error('Could not copy — select the link and copy it manually.');
    }
  }

  return (
    <div className="rounded-[10px] border border-line2 p-3">
      <p className="text-[13px] font-semibold">{label}</p>
      <p className="mt-0.5 text-[12px] text-muted">{hint}</p>
      <div className="mt-2 flex gap-2">
        <input
          readOnly
          value={link}
          aria-label={label}
          className="input font-mono text-[12px]"
          onFocus={(e) => e.target.select()}
        />
        <button type="button" className="btn btn-primary shrink-0" onClick={copy} aria-label={`Copy link: ${label}`}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

export function InviteDialog({
  retroId,
  open,
  onClose,
}: {
  retroId: string;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['invites', retroId],
    queryFn: () => api.retros.invites(retroId),
    enabled: open,
  });

  const rotate = useMutation({
    mutationFn: () => api.retros.rotateInvites(retroId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['invites', retroId] }),
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <Modal open={open} onClose={onClose} title="Invite your team">
      {isLoading ? <Spinner label="Preparing links…" /> : null}
      {error ? <p className="text-sm text-rose-600">{(error as Error).message}</p> : null}
      {data ? (
        <div className="space-y-3">
          <CopyRow
            label="Join as a team member"
            hint="They join the retro and the team, and can see past retrospectives. A free account is required."
            link={data.memberLink}
          />
          <CopyRow
            label="Join as a guest"
            hint="No account needed. Guests take part in this session only and cannot see team history."
            link={data.guestLink}
          />
          <div className="pt-1">
            <button
              type="button"
              className="btn w-full text-sm"
              // Rotation invalidates both links with no further warning; a
              // confirm is the right amount of friction for an irreversible
              // action whose only purpose is damage control.
              onClick={() => {
                if (window.confirm('Generate new invite links? The existing member and guest links will stop working immediately.')) {
                  rotate.mutate();
                }
              }}
              disabled={rotate.isPending}
            >
              {rotate.isPending ? 'Regenerating…' : 'Regenerate links'}
            </button>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
